//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/* eslint-disable prettier/prettier */
import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Card,
  Col,
  Container,
  Row,
  Table,
  Pagination,
  Form,
  Button,
  Modal,
} from "react-bootstrap";
import { useTable, usePagination } from "react-table";
import { Context } from "../../common/context/Context";
import { CandidatePageActions } from "../../candidatePageActions.js";
import { Link, useLocation } from "react-router-dom";
import { AddManageGAActions } from "../addManageGAActions";
import Select from "react-select";
import { ManageClientsActions } from "../../clients/manageClients/manageClientsActions";
import AlertMessage from "../../common/alertMessages/alertMessages";
import ErrorAlertMessage from "../../common/alertMessages/errorAlertMessage";
import { getCandidatesSearchCMSContent } from "../../common/helpers/candidateCmsHelper";
import { getPageContentLabel } from "../../common/helpers/cmsLabelHelper";

const CandidatesListPagination = ({ columns, data, cmsData }) => {

  const fetchCommonCMSLabel = (key) =>
    getPageContentLabel(cmsData, "CommonComponent", key);

  const returnPageIndexText = (currentPage, TotalPage) => {
    const pageText = fetchCommonCMSLabel("Common.Page");
    let replaceText = pageText.replace('Page', '<span>Page</span>').replace('#Currentpage#', '<strong>' + currentPage + '</strong>').replace('#Totalpage#', '<strong>' + TotalPage + '</strong>');
    return (
      <div className="me-2 mt-1" dangerouslySetInnerHTML={{ __html: replaceText }} />
    )
  }

  // let tableLabels = cmsData && cmsData;

  // const fetchAccDetailsLabels = (key) => {
  //   let refCmsContent = tableLabels && tableLabels?.Properties;
  //   let label = "";
  //   if (refCmsContent?.Labels) {
  //     refCmsContent?.Labels.map((x) => {
  //       if (x.Key === key) {
  //         label = x.Phrase;
  //       }
  //     });
  //   }
  //   return label;
  // };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
    },
    usePagination
  );

  return (
    <Card>
      <Card.Header>
        <Card.Title className="mb-0">
          {cmsData?.title}
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <Table className="mb-0" {...getTableProps()} responsive>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>

        <Row className="mt-3">
          <Col md="6" className="d-flex flex-lg-row flex-column">
            {returnPageIndexText(pageIndex + 1, pageOptions.length)}
            <div className="me-2">
              <span className="me-2">
                {fetchCommonCMSLabel("Common.Show")}
              </span>
              <Form.Select
                className="d-inline-block w-auto"
                value={pageSize}
                aria-label="pageNumber"
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
              >
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="me-2">
              <span className="ms-3 me-2">
                {fetchCommonCMSLabel("Common.GoToPage")}
              </span>
              <Form.Control
                className="d-inline-block"
                type="number"
                aria-label="number"
                min="1"
                defaultValue={pageIndex + 1}
                onChange={(e) => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                  gotoPage(page);
                }}
                style={{ width: "75px" }}
              />
            </div>
          </Col>
          <Col md="6" className="mt-1">
            <Pagination className="float-lg-end float-md-end float-left">
              <Pagination.First
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              />
              <Pagination.Prev
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              />
              <Pagination.Next
                onClick={() => nextPage()}
                disabled={!canNextPage}
              />
              <Pagination.Last
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              />
            </Pagination>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const AccountDetailsScreen = () => {
  const context = useContext(Context);
  const location = useLocation();
  const [accDetailsJson, setAccDetailsJson] = useState({});
  const [selectedServiceOffice, setSelectedServiceOffice] = useState(null);
  const [editExistingClientsJson, setEditExistingClientsJson] = useState({});
  const [checkList, setCheckList] = useState([]);
  const [uncheckedList, setUncheckedList] = useState([]);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    showAlert: false,
  });
  const [errorAlert, setErrorAlert] = useState({
    showAlert: false,
  });
  const [adminGenericDetailsScreenCMS, setAdminGenericDetailsScreenCMS] = useState({ title: "", description: "", tabNavigationTitle: "", pageContent: {} })


  const getAdminGenericDetailsScreenCMSContent = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
    const cmsContent = await getCandidatesSearchCMSContent();

    if (cmsContent) {
      setAdminGenericDetailsScreenCMS(cmsContent);
    } else {
      refErrorMessageAlert.showAlert = true;
      setErrorAlert(refErrorMessageAlert);
      window.scrollTo(0, 0);
    }
  }

  const fetchGenericDetailsScreenLabel = (key) =>
    getPageContentLabel(adminGenericDetailsScreenCMS, "GenericDetailsScreen", key);

  const fetchCreateAccountLabel = (key) =>
    getPageContentLabel(
      adminGenericDetailsScreenCMS,
      "GenericAccounts.CreateAccountLabel",
      key
    );

  const fetchEditExistingClientLabel = (key) =>
    getPageContentLabel(adminGenericDetailsScreenCMS, "Edit-Existing-Client", key);

  const fetchCommonCMSLabel = (key) =>
    getPageContentLabel(adminGenericDetailsScreenCMS, "CommonComponent", key);




  const tableColumns = [
    {
      id: "GenericDetailsScreen.GenericAccountsHeader",
      Header: fetchGenericDetailsScreenLabel(
        "GenericDetailsScreen.GenericAccountsHeader"
      ),
      accessor: (values) => {
        const { firstName, lastName } = values;
        let name = "";
        if (firstName && lastName) {
          name = lastName + "," + firstName;
        } else if (firstName && !lastName) {
          name = firstName;
        } else if (!firstName && lastName) {
          name = lastName;
        }
        return name;
      },
      Cell: ({ row, value }) => (
        <Link
          to={"/candidateProfile"}
          state={{
            candidateId: row?.original?.candidateId,
            referenceCode: row?.original?.referenceCode,
            firstName: row?.original?.firstName,
            lastName: row?.original?.lastName,
          }}
        >
          {value}
        </Link>
      ),
    },
    {
      id: "GenericDetailsScreen.CandidatesEmail",
      Header: fetchGenericDetailsScreenLabel("GenericDetailsScreen.CandidatesEmail"),
      accessor: "emailAddress",
    },
    {
      id: "GenericDetailsScreen.CandidateProgramClass",
      Header: fetchGenericDetailsScreenLabel(
        "GenericDetailsScreen.ProgramClass"
      ),
      accessor: "programClass",
    },
    {
      id: "GenericDetailsScreen.CandidateServiceOffice",
      Header: fetchGenericDetailsScreenLabel(
        "GenericDetailsScreen.CandidateServiceOffice"
      ),
      accessor: "serviceOffice",
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    //fetchAccDetailsJson();
    getAdminGenericDetailsScreenCMSContent();
  }, []);

  let CountryListData = context.countryList && context.countryList;
  const [validated, setValidated] = useState(false);
  const [serviceOfficeList, setServiceOfficeList] = useState([]);
  const [accDetails, setAccDetails] = useState({});
  const [candidateDetailsList, setCandidateDetailsList] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [serviceOfficeChanged, setServiceOfficeChanged] = useState(false);
  const [createGenericAcc, setCreateGenericAcc] = useState({
    GenericAccountId: "",
    ReferenceCode: "",
    ProgramDuration: "",
    NumberOfAccountsAllowed: 0,
    PersonaId: 0,
    Persona: "",
    ProgramClassid: 0,
    ProgramClass: "",
    ProgramSetupId: "",
    EndDate: "",
    updateServiceOffice: [],
  });
  const [programList, setProgramList] = useState({
    tools: [],
    assessmentsTasks: [],
    isEventsWebinarsPresent: false,
    isDoceboTilePresent: false,
    eventsWebinars: [],
    doceboTiles: [],
  });
  const [clickCheckBox, setClickCheckBox] = useState(false);
  const [featuresList, setFeaturesList] = useState([]);

  const fetchProgramList = async (programSetUpId) => {
    let refProgramList = JSON.parse(JSON.stringify(programList));
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    try {
      const res = await ManageClientsActions.GetProgramSetupList(programSetUpId);
      if (res.status === 200 || res.status === 204) {
        const resData = res.data || [];
        setFeaturesList(resData);
        // Helper to filter by itemType or itemName
        const filterBy = (key, value) => resData.filter((x) => x[key] === value);
        refProgramList.tools = filterBy("itemType", "Tool");
        refProgramList.assessmentsTasks = filterBy("itemType", "Assessment");
        refProgramList.eventsWebinars = filterBy("itemName", "EventsPage");
        refProgramList.doceboTiles = filterBy("itemName", "DoceboTile");
        refProgramList.MyCoachPage = filterBy("itemName", "MyCoachPage");
        refProgramList.isEventsWebinarsPresent = refProgramList.eventsWebinars.length > 0;
        refProgramList.isDoceboTilePresent = refProgramList.doceboTiles.length > 0;
        setProgramList(refProgramList);
        setClickCheckBox(false);
      } else {
        handleProgramListError(refErrorMessageAlert);
      }
    } catch (err) {
      console.log(err);
    }
  };
  function handleProgramListError(refErrorMessageAlert) {
    refErrorMessageAlert.showAlert = true;
    setErrorAlert(refErrorMessageAlert);
    window.scrollTo(0, 0);
  }

  const fetchProgramListAfterDelete = async (programClassId, countryCode) => {
    let refProgramList = JSON.parse(JSON.stringify(programList));
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    await AddManageGAActions.GetFeaturesList(programClassId, countryCode).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          let resData = res.data && res.data;
          setFeaturesList(resData);

          let toolsList =
            resData && resData.filter((x) => x.itemType == "Tool");
          let assessmentList =
            resData && resData.filter((x) => x.itemType == "Assessment");
          let eventsList =
            resData && resData.filter((x) => x.itemName == "EventsPage");
          let doceboList =
            resData && resData.filter((x) => x.itemName == "DoceboTile");

          refProgramList.tools = toolsList && toolsList;
          refProgramList.assessmentsTasks = assessmentList && assessmentList;
          refProgramList.eventsWebinars =
            eventsList && eventsList.length > 0 ? true : false;
          refProgramList.doceboTile =
            doceboList && doceboList.length > 0 ? true : false;
          setProgramList(refProgramList);
          setClickCheckBox(false);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const fetchGenericAccDetails = () => {
    let { viewRefCode } = location && location.state;
    let { isFromViewGa } = location && location.state;

    let { addRefCode } = location && location.state;
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
    let referenceCode = isFromViewGa ? viewRefCode : addRefCode;

    CandidatePageActions.GetGenericAccountByRefCode(referenceCode).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          let resData = res.data && res.data;

          fetchProgramListDetails(resData?.programSetupId);
          setAccDetails(resData);
          getServiceOffice(resData && resData?.countryCode);

          let start = resData && resData?.startDate;
          start = start.substring(0, 10);
          setStartDate(start);

          let end = resData && resData?.endDate;
          end = end.substring(0, 10);
          setEndDate(end);

          let serviceOfficeLists = resData && resData?.serviceOfficeModels;
          serviceOfficeLists =
            serviceOfficeLists &&
            serviceOfficeLists.map((x) => ({
              label: x.rightOfficeName,
              value: {
                rightOfficeName: x.rightOfficeName,
                rightOfficeId: x.rightOfficeId,
                countryCode: resData && resData.countryCode,
              },
            }));
          setSelectedServiceOffice(serviceOfficeLists);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const fetchServiceOfficeList = () => {
    let selectedServiceOfficeList =
      selectedServiceOffice && selectedServiceOffice;
    let serviceOfficeArray = selectedServiceOfficeList.map((x) => ({
      RightOfficeId: x.value.rightOfficeId,
      RightOfficeName: x.value.rightOfficeName,
    }));
    return serviceOfficeArray;
  };

  const fetchServiceOfficeNoChange = (serviceOfficeData) => {
    let serviceOfficeArray =
      serviceOfficeData &&
      serviceOfficeData.map((x) => ({
        RightOfficeId: x.value.rightOfficeId,
        RightOfficeName: x.value.rightOfficeName,
      }));
    return serviceOfficeArray;
  };

  const updateGenericAccount = (e) => {
    let refAlert = JSON.parse(JSON.stringify(alert));

    const form = e.currentTarget;
    if (!form.checkValidity()) {
      e.preventDefault();
      e.stopPropagation();
    }
    setValidated(true);

    handleUpdateClose();

    let oldProgramClassId = accDetails && accDetails?.programClassId;
    let ProgramSetupId = accDetails && accDetails?.programSetupId;
    let programClassId =
      context.rtProgramClass &&
      context.rtProgramClass.length > 0 &&
      context.rtProgramClass.find(
        (x) => x.programclassId == document.getElementById("programClass").value
      );
    programClassId = programClassId && programClassId.programclassId;

    !(programClassId == oldProgramClassId) && deleteProgramList(ProgramSetupId);

    let personaName =
      context.rtPersona &&
      context.rtPersona.length > 0 &&
      context.rtPersona.find(
        (x) => x.rtPersonaId == document.getElementById("rtPersona").value
      );
    personaName = personaName && personaName.rtPersonaName;

    let ProgramClassName =
      context.rtProgramClass &&
      context.rtProgramClass.length > 0 &&
      context.rtProgramClass.find(
        (x) => x.programclassId == document.getElementById("programClass").value
      );
    ProgramClassName = ProgramClassName && ProgramClassName.programClass;

    let refCreateGenericAcc = JSON.parse(JSON.stringify(createGenericAcc));

    refCreateGenericAcc.ReferenceCode =
      document.getElementById("registrationCode").value;
    refCreateGenericAcc.EndDate = document.getElementById("endDate").value;
    refCreateGenericAcc.NumberOfAccountsAllowed = parseInt(
      document.getElementById("accountsCount").value
    );
    refCreateGenericAcc.ProgramDuration =
      document.getElementById("programDuration").value;
    refCreateGenericAcc.PersonaId = parseInt(
      document.getElementById("rtPersona").value
    );
    refCreateGenericAcc.ProgramClassid = parseInt(
      document.getElementById("programClass").value
    );
    refCreateGenericAcc.ProgramClass = ProgramClassName;
    refCreateGenericAcc.Persona = personaName;
    refCreateGenericAcc.updateServiceOffice = serviceOfficeChanged
      ? fetchServiceOfficeList()
      : fetchServiceOfficeNoChange(selectedServiceOffice);
    refCreateGenericAcc.ProgramSetupId =
      accDetails && accDetails?.programSetupId;
    refCreateGenericAcc.GenericAccountId =
      accDetails && accDetails?.serviceOfficeModels[0]?.genericAccountId;

    setCreateGenericAcc(refCreateGenericAcc);

    CandidatePageActions.UpdateGenericAccount(refCreateGenericAcc).then(
      (res) => {
        if (res.status === 200) {
          let programSetUpId = accDetails && accDetails?.programSetupId;
          let savedList = uncheckedList && uncheckedList;
          clickCheckBox && saveProgramList(programSetUpId, savedList);
          refAlert.showAlert = true;
          refAlert.type = "success";
          refAlert.message = fetchGenericDetailsScreenLabel(
            "GenericDetailsScreen.UpdateSuccessLabel"
          );
        } else if (
          res.status === 500 ||
          res.status === 401 ||
          res.status === 400
        ) {
          refAlert.showAlert = true;
          refAlert.type = "danger";
          refAlert.message = fetchGenericDetailsScreenLabel(
            "GenericDetailsScreen.UpdateErrorLabel"
          );
        } else if (res.status === 204) {
          refAlert.showAlert = true;
          refAlert.type = "danger";
          refAlert.message = res
            ? res
            : fetchGenericDetailsScreenLabel(
              "GenericDetailsScreen.UpdateNoMessageLabel"
            );
        }
        setAlert(refAlert);
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const [error, setError] = useState({
    noOfAcc: "",
    programDuration: "",
    country: false,
  });

  const fetchServiceOfficesForMultiSelect = (serviceOffices) => {
    let serviceOfficeOptions = serviceOffices?.map((item) => ({
      label: item?.rightOfficeName,
      value: JSON.parse(JSON.stringify(item)),
    }));
    return serviceOfficeOptions;
  };

  const handleUserServiceOfficeChange = (value) => {
    setError({
      ...error,
      country: false,
    });
    setServiceOfficeChanged(true);
    setSelectedServiceOffice(value);
  };

  const handleNoOfAcc = (e, registeredAcc) => {
    let noOfAcc = e.target.value;
    let errorMessage = "";
    let numbers = /^[0-9]+$/;
    let decimal = /^[-+]?[0-9]+\.[0-9]+$/;
    if (!noOfAcc.match(numbers)) {
      errorMessage = fetchCreateAccountLabel("GenericAccounts.onlyNumbers");
    } else if (noOfAcc.match(decimal)) {
      errorMessage = fetchCreateAccountLabel(
        "GenericAccounts.decimalValidation"
      );
    } else if (noOfAcc < registeredAcc) {
      errorMessage = fetchCreateAccountLabel(
        "GenericAccounts.greaterThanRegisteredAcc"
      );
    } else if (noOfAcc < 1) {
      errorMessage = fetchCreateAccountLabel(
        "GenericAccounts.minValue1Error"
      );
    } else if (noOfAcc > 999) {
      errorMessage = fetchCreateAccountLabel(
        "GenericAccounts.maxValue999Error"
      );
    }
    setError({
      ...error,
      [e.target.name]: errorMessage,
    });
  };

  const handleProgramDuration = (e) => {
    let programDays = e.target.value;
    let errorMessage = "";

    let numbers = /^[0-9]+$/;
    let decimal = /^[-+]?[0-9]+\.[0-9]+$/;

    if (!programDays.match(numbers)) {
      errorMessage = fetchCreateAccountLabel("GenericAccounts.onlyNumbers");
    } else if (programDays.match(decimal)) {
      errorMessage = fetchCreateAccountLabel(
        "GenericAccounts.decimalValidation"
      );
    } else if (programDays < 1) {
      errorMessage = fetchCreateAccountLabel(
        "GenericAccounts.minValue1Error"
      );
    } else if (programDays > 999) {
      errorMessage = fetchCreateAccountLabel(
        "GenericAccounts.maxValue999Error"
      );
    }
    setError({
      ...error,
      [e.target.name]: errorMessage,
    });
  };

  const deleteGenericAccount = () => {
    let refAlert = JSON.parse(JSON.stringify(alert));
    let ProgramSetupId = accDetails && accDetails?.programSetupId;
    let { viewRefCode } = location && location.state;
    handleDeleteClose();
    CandidatePageActions.DeleteGenericAccount(viewRefCode).then(
      (res) => {
        if (res.status === 200) {
          refAlert.showAlert = true;
          refAlert.type = "success";
          deleteProgramList(ProgramSetupId);
          refAlert.message = fetchGenericDetailsScreenLabel(
            "GenericDetailsScreen.DeleteSuccessLabel"
          );
        } else if (
          res.status === 500 ||
          res.status === 401 ||
          res.status === 400
        ) {
          refAlert.showAlert = true;
          refAlert.type = "danger";
          refAlert.message = fetchGenericDetailsScreenLabel(
            "GenericDetailsScreen.DeleteErrorLabel"
          );
        } else if (res.status === 204) {
          refAlert.showAlert = true;
          refAlert.type = "danger";
          refAlert.message = res
            ? res
            : fetchGenericDetailsScreenLabel(
              "GenericDetailsScreen.DeleteNoMessageLabel"
            );
        }
        setAlert(refAlert);
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const getServiceOffice = async (countryCode) => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    CandidatePageActions.GetServiceOffice(countryCode).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          setServiceOfficeList(res.data && res.data);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const unselectCheckBox = (e, item) => {
    setClickCheckBox(true);

    let listFromRes = featuresList && featuresList.length > 0 && featuresList;
    let updatedArray =
      listFromRes &&
      listFromRes.map((x) =>
        x.itemDetailsId == item.itemDetailsId
          ? { ...x, isActive: !x.isActive }
          : x
      );
    setUncheckedList(updatedArray && updatedArray);
    setFeaturesList(updatedArray && updatedArray);
    let checkedArray =
      updatedArray && updatedArray.filter((x) => x.isActive == true);
    checkedArray = checkedArray && checkedArray.map((x) => x.itemDetailsId);
    setCheckList(checkedArray && checkedArray);
  };

  const fetchCandidateDetails = () => {
    let { viewRefCode } = location && location.state;
    let { isFromViewGa } = location && location.state;

    let { addRefCode } = location && location.state;
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
    let referenceCode = isFromViewGa === true ? viewRefCode : addRefCode;

    CandidatePageActions.GetCandidateDetailsByRefCode(referenceCode).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          let resData = res?.data;
          setCandidateDetailsList(resData.length > 0 ? resData : []);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };


  const saveProgramList = async (programSetUpId, saveList) => {
    let payload = saveList && saveList;
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    await AddManageGAActions.SaveFeaturesList(programSetUpId, payload).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          fetchProgramList(programSetUpId);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const fetchProgramListDetails = (ProgramSetupId) => {
    fetchProgramList(ProgramSetupId);
    //fetchEditExistingClientsJson();
  };

  const handleProgramClass = () => {
    let programClassId =
      context.rtProgramClass &&
      context.rtProgramClass.length > 0 &&
      context.rtProgramClass.find(
        (x) => x.programclassId == document.getElementById("programClass").value
      );
    programClassId = programClassId && programClassId.programclassId;

    let countryStandardCode =
      CountryListData &&
      CountryListData.length > 0 &&
      CountryListData.find(
        (x) => x.title == document.getElementById("country").value
      );
    countryStandardCode =
      countryStandardCode && countryStandardCode.standardCodeValue;

    let countryCode = document.getElementById("country").value;
    let programSetUpId = accDetails && accDetails?.programSetupId;
    let oldProgramClassId = accDetails && accDetails?.programClassId;
    let oldCountryCode = accDetails && accDetails?.country;

    programClassId == oldProgramClassId && countryCode == oldCountryCode
      ? fetchProgramList(programSetUpId)
      : fetchProgramListAfterDelete(programClassId, countryStandardCode);
  };

  const deleteProgramList = async (ProgramSetupId) => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    await ManageClientsActions.DeleteProgramSetupList(ProgramSetupId).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchGenericAccDetails();
    fetchCandidateDetails();
  }, []);

  const handleUpdateClose = () => {
    setShowUpdateModal(false);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
  };

  const handleCloseAndRetainAccDetails = () => {
    setShowUpdateModal(false);
    fetchGenericAccDetails();
  };

  const handleDeleteCloseRetainAcc = () => {
    setShowDeleteModal(false);
    fetchGenericAccDetails();
  };

  const showModal = () => {
    let refAlert = JSON.parse(JSON.stringify(alert));
    refAlert.showAlert = false;
    setAlert(refAlert);
  };

  const showErrorModal = () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
    refErrorMessageAlert.showAlert = false;
    setErrorAlert(refErrorMessageAlert);
  };

  const handleUpdate = (e) => {
    if (selectedServiceOffice && selectedServiceOffice?.length === 0) {
      setError({
        ...error,
        country: true,
      });
      return;
    }
    setShowUpdateModal(true);
  };

  useEffect(() => {
    setError({ ...error, country: false });
  }, [selectedServiceOffice]);

  return (
    <React.Fragment>
      <Helmet title="Cards" />
      <Container fluid className="p-0">
        <h1 className="h3 mb-3">{accDetails && accDetails?.accountName}</h1>

        {errorAlert?.showAlert && (
          <ErrorAlertMessage
            alertMessage="AllPages.ErrorAlertMessage"
            showAlert={showErrorModal}
          />
        )}

        <Row>
          <Col lg="12">
            <Card>
              <Card.Body>
                <Form noValidate validated={validated}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {fetchGenericDetailsScreenLabel(
                        "GenericDetailsScreen.RegistrationCode"
                      )}
                    </Form.Label>
                    <Form.Control
                      name="genericRegistrationCode"
                      placeholder={fetchGenericDetailsScreenLabel(
                        "GenericDetailsScreen.RegistrationCode"
                      )}
                      type="text"
                      id="registrationCode"
                      defaultValue={accDetails && accDetails?.referenceCode}
                      readOnly
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {fetchGenericDetailsScreenLabel("GenericDetailsScreen.StartDate")}
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      placeholder={fetchGenericDetailsScreenLabel(
                        "GenericDetailsScreen.StartDate"
                      )}
                      id="startDate"
                      value={startDate && startDate}
                      readOnly
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {fetchGenericDetailsScreenLabel(
                        "GenericDetailsScreen.ExpirationDate"
                      )}
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="expirationDate"
                      placeholder={fetchGenericDetailsScreenLabel(
                        "GenericDetailsScreen.ExpirationDate"
                      )}
                      id="endDate"
                      required
                      defaultValue={endDate && endDate}
                      min={
                        document.getElementById("startDate") &&
                        document.getElementById("startDate").value
                      }
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {fetchGenericDetailsScreenLabel(
                        "GenericDetailsScreen.NumberOfAccounts"
                      )}
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      name="noOfAcc"
                      placeholder={fetchGenericDetailsScreenLabel(
                        "GenericDetailsScreen.NumberOfAccounts"
                      )}
                      id="accountsCount"
                      required
                      isInvalid={!!error.noOfAcc}
                      defaultValue={
                        accDetails && accDetails?.numberOfAccountsAllowed
                      }
                      onChange={(e) =>
                        handleNoOfAcc(
                          e,
                          accDetails && accDetails?.numberOfAccountsAllowed
                        )
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {error.noOfAcc}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      {fetchGenericDetailsScreenLabel(
                        "GenericDetailsScreen.ProgramDuration"
                      )}
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      name="programDuration"
                      placeholder={fetchGenericDetailsScreenLabel(
                        "GenericDetailsScreen.ProgramDuration"
                      )}
                      id="programDuration"
                      required
                      defaultValue={accDetails && accDetails?.programDuration}
                      isInvalid={!!error.programDuration}
                      onChange={(e) => handleProgramDuration(e)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {error.programDuration}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      {fetchGenericDetailsScreenLabel("GenericDetailsScreen.RTPersona")}
                    </Form.Label>
                    <Form.Select
                      aria-label={fetchCreateAccountLabel(
                        "GenericDetailsScreen.personaOption"
                      )}
                      id="rtPersona"
                      name="rtPersona"
                    >
                      <option>
                        {fetchCreateAccountLabel(
                          "GenericDetailsScreen.personaOption"
                        )}
                      </option>
                      {context.rtPersona &&
                        context.rtPersona.length > 0 &&
                        context.rtPersona.map((item) => (
                          <option
                            selected={
                              accDetails &&
                              accDetails?.persona == item?.rtPersonaName
                            }
                            id={item?.rtPersonaName}
                            value={item?.rtPersonaId}
                          >
                            {item?.rtPersonaName}
                          </option>
                        ))}
                    </Form.Select>
                    <div className="invalid-feedback">
                      {fetchCreateAccountLabel(
                        "GenericAccounts.personaValidation"
                      )}
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      {fetchCreateAccountLabel("GenericAccounts.Country")}
                    </Form.Label>
                    <Form.Control
                      name="country"
                      id="country"
                      aria-label="country"
                      value={accDetails && accDetails?.country}
                      readOnly
                    />
                    <div className="invalid-feedback">
                      {fetchCreateAccountLabel(
                        "GenericAccounts.countryValidation"
                      )}
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      {fetchGenericDetailsScreenLabel(
                        "GenericDetailsScreen.ProgramClass"
                      )}
                    </Form.Label>
                    <Form.Select
                      aria-label="Program Class Select"
                      id="programClass"
                      name="programClass"
                      onChange={(e) => handleProgramClass(e)}
                    >
                      <option>
                        {fetchCreateAccountLabel(
                          "GenericAccounts.programClassOption"
                        )}
                      </option>
                      {context.rtProgramClass &&
                        context.rtProgramClass.length > 0 &&
                        context.rtProgramClass.map((item) => (
                          <option
                            selected={
                              accDetails &&
                              accDetails?.programClass == item?.programClass
                            }
                            value={item?.programclassId}
                          >
                            {item?.programClass}
                          </option>
                        ))}
                    </Form.Select>
                    <div className="invalid-feedback">
                      {fetchCreateAccountLabel(
                        "GenericAccounts.programClassValidation"
                      )}
                    </div>
                  </Form.Group>

                  {document.getElementById("programClass") &&
                    document.getElementById("programClass").value && (
                      <div>
                        <Card mt-3>
                          <Card.Title className="mb-0 mt-4">
                            {fetchCreateAccountLabel(
                              "GenericAccounts.FeaturesHeading"
                            )}
                          </Card.Title>
                          {programList.tools && programList.tools.length > 0 && (
                            <div>
                              <Card.Header>
                                <Card.Title className="mb-0 mt-4">
                                  {fetchEditExistingClientLabel(
                                    "EditExistingClient.Tools"
                                  )}
                                </Card.Title>
                              </Card.Header>

                              <Card.Body className="text-left">
                                <Row>
                                  {programList.tools.map((item) => (
                                    <Col md="4" xs="12" key={item?.itemDetailsId}>
                                      <Form.Group className="mb-3">
                                        <Form.Check
                                          style={{ marginRight: "15px" }}
                                          label={item?.itemName}
                                          type="checkbox"
                                          aria-label="checkbox"
                                          checked={
                                            clickCheckBox
                                              ? !!checkList?.includes(item?.itemDetailsId)
                                              : !!item?.isActive
                                          }
                                          onClick={(e) =>
                                            unselectCheckBox(e, item)
                                          }
                                        />
                                      </Form.Group>
                                    </Col>
                                  ))}
                                </Row>
                              </Card.Body>
                            </div>
                          )}
                          {programList.assessmentsTasks &&
                            programList.assessmentsTasks.length > 0 && (
                              <div>
                                <Card.Header>
                                  <Card.Title className="mb-0 mt-4">
                                    {fetchEditExistingClientLabel(
                                      "EditExistingClient.AssessmentsTasks"
                                    )}
                                  </Card.Title>
                                </Card.Header>

                                <Card.Body className="text-left">
                                  <Row>
                                    {programList.assessmentsTasks.map(
                                      (item) => (
                                        <Col md="4" xs="12" key={item?.itemDetailsId}>
                                          <Form.Group className="mb-3">
                                            <Form.Check
                                              style={{ marginRight: "15px" }}
                                              label={item?.itemName}
                                              type="checkbox"
                                              aria-label="checkbox"
                                              checked={
                                                clickCheckBox
                                                  ? !!checkList?.includes(item?.itemDetailsId)
                                                  : !!item?.isActive
                                              }
                                              onClick={(e) =>
                                                unselectCheckBox(e, item)
                                              }
                                            />
                                          </Form.Group>
                                        </Col>
                                      )
                                    )}
                                  </Row>
                                </Card.Body>
                              </div>
                            )}
                          {((programList &&
                            programList?.isEventsWebinarsPresent) ||
                            (programList &&
                              programList?.isDoceboTilePresent)) && (
                              <div>
                                <Card.Header>
                                  <Card.Title className="mb-0 mt-4">
                                    {" "}
                                    {fetchEditExistingClientLabel(
                                      "EditExistingClient.EventsWebinars"
                                    )}
                                  </Card.Title>
                                </Card.Header>
                                <Card.Body
                                  className="text-left"
                                  style={{ padding: "0 30px" }}
                                >
                                  <div>
                                    {programList &&
                                      programList?.isEventsWebinarsPresent && (
                                        <Form.Group className="mb-3">
                                          <Form.Check
                                            style={{ marginRight: "15px" }}
                                            label={fetchEditExistingClientLabel(
                                              "EditExistingClient.AllEventsWebinars"
                                            )}
                                            type="checkbox"
                                            aria-label="checkbox"
                                            checked={
                                              clickCheckBox
                                                ? !!checkList?.includes(programList?.eventsWebinars?.[0]?.itemDetailsId)
                                                : !!programList?.eventsWebinars?.[0]?.isActive
                                            }
                                            onClick={(e) =>
                                              unselectCheckBox(
                                                e,
                                                programList?.eventsWebinars[0]
                                              )
                                            }
                                          />
                                        </Form.Group>
                                      )}
                                    {programList &&
                                      programList?.isDoceboTilePresent && (
                                        <Form.Group className="mb-3">
                                          <Form.Check
                                            style={{ marginRight: "15px" }}
                                            label={fetchEditExistingClientLabel(
                                              "EditExistingClient.ClientEventsWebinars"
                                            )}
                                            type="checkbox"
                                            aria-label="checkbox"
                                            checked={
                                              clickCheckBox
                                                ? !!checkList?.includes(programList?.doceboTiles?.[0]?.itemDetailsId)
                                                : !!programList?.doceboTiles?.[0]?.isActive
                                            }
                                            onClick={(e) =>
                                              unselectCheckBox(
                                                e,
                                                programList?.doceboTiles[0]
                                              )
                                            }
                                          />
                                        </Form.Group>
                                      )}
                                  </div>
                                </Card.Body>
                              </div>
                            )}
                          {programList && programList?.MyCoachPage && programList?.MyCoachPage.length > 0 &&
                            <div>
                              <Card.Header>
                                <Card.Title className="mb-0 mt-4">{fetchEditExistingClientLabel("EditExistingClient.CoachMeeting")}</Card.Title>
                              </Card.Header>

                              <Card.Body className="text-left" style={{ display: "flex", flexWrap: "wrap", padding: "0 30px" }}>
                                <Row>
                                  <Form.Group className="mb-3">
                                    <Form.Check
                                      style={{ marginRight: "15px" }}
                                      label={fetchEditExistingClientLabel("EditExistingClient.EnableMyCoachpage")}
                                      type="checkbox"
                                      aria-label="Checkbox"
                                      disabled={!context.permissionsList?.includes("Clients.editclientprogramsetup")}
                                      checked={clickCheckBox
                                        ? !!checkList?.includes(programList?.MyCoachPage?.[0]?.itemDetailsId)
                                        : !!programList?.MyCoachPage?.[0]?.isActive}
                                      onClick={(e) => unselectCheckBox(e, programList?.MyCoachPage[0])} />
                                  </Form.Group>
                                </Row>
                              </Card.Body>
                            </div>
                          }
                        </Card>
                      </div>
                    )}

                  <Form.Group className="mb-3">
                    <Form.Label>
                      {fetchCreateAccountLabel(
                        "GenericAccounts.ServiceOffice"
                      )}
                    </Form.Label>
                    <Select
                      name="country"
                      options={
                        serviceOfficeList &&
                        fetchServiceOfficesForMultiSelect(serviceOfficeList)
                      }
                      isMulti
                      id="serviceOffice"
                      value={selectedServiceOffice}
                      aria-label="Role Office Select"
                      onChange={(selected) => {
                        handleUserServiceOfficeChange(selected);
                      }}
                    />
                    {error?.country && (
                      <div style={{ color: "red" }}>
                        {fetchCreateAccountLabel(
                          "GenericAccounts.serviceOfficeValidation"
                        )}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      {fetchCreateAccountLabel(
                        "GenericAccounts.RequestedBy"
                      )}
                    </Form.Label>
                    <Form.Control
                      id="requestedBy"
                      name="requestedBy"
                      aria-label="requestedBy"
                      readOnly
                      defaultValue={accDetails && accDetails?.requestedBy}
                    />
                  </Form.Group>
                  {alert?.showAlert && (
                    <AlertMessage
                      alertMessage={alert?.message}
                      alertType={alert?.type}
                      showAlert={showModal}
                    />
                  )}
                  {context?.permissionsList &&
                    context?.permissionsList.includes(
                      "Generic Account.editgenericaccount"
                    ) && (
                      <div>
                        <Button
                          className="m-3"
                          variant="primary"
                          onClick={(e) => handleUpdate(e)}
                        >
                          {fetchCommonCMSLabel("Common.Update")}
                        </Button>
                        {showUpdateModal && (
                          <Card>
                            <React.Fragment>
                              <Modal
                                show={showUpdateModal}
                                onHide={(e) => handleUpdateClose(e)}
                              >
                                <Modal.Header closeButton></Modal.Header>
                                <Modal.Body className="text-center m-3">
                                  <p className="mb-0">
                                    {fetchGenericDetailsScreenLabel(
                                      "GenericDetailsScreen.warningMessage"
                                    )}
                                  </p>
                                </Modal.Body>
                                <Modal.Footer>
                                  <Button
                                    variant="secondary"
                                    onClick={(e) =>
                                      handleCloseAndRetainAccDetails(e)
                                    }
                                  >
                                    {fetchCommonCMSLabel(
                                      "Common.Cancel"
                                    )}
                                  </Button>{" "}
                                  <Button
                                    variant="primary"
                                    onClick={(e) => updateGenericAccount(e)}
                                  >
                                    {fetchCommonCMSLabel(
                                      "Common.OK"
                                    )}
                                  </Button>
                                </Modal.Footer>
                              </Modal>
                            </React.Fragment>
                          </Card>
                        )}
                        <Button
                          variant="secondary"
                          onClick={() => setShowDeleteModal(true)}
                        >
                          {fetchCommonCMSLabel(
                            "Common.Delete"
                          )}
                        </Button>
                        {showDeleteModal && (
                          <Card>
                            <React.Fragment>
                              <Modal
                                show={showDeleteModal}
                                onHide={(e) => handleDeleteClose(e)}
                              >
                                <Modal.Header closeButton></Modal.Header>
                                <Modal.Body className="text-center m-3">
                                  <p className="mb-0">
                                    {fetchGenericDetailsScreenLabel(
                                      "GenericDetailsScreen.deleteMessage"
                                    )}
                                  </p>
                                </Modal.Body>
                                <Modal.Footer>
                                  <Button
                                    variant="secondary"
                                    onClick={(e) =>
                                      handleDeleteCloseRetainAcc(e)
                                    }
                                  >
                                    {fetchCommonCMSLabel(
                                      "Common.Cancel"
                                    )}
                                  </Button>{" "}
                                  <Button
                                    variant="primary"
                                    onClick={(e) => deleteGenericAccount(e)}
                                  >
                                    {fetchCommonCMSLabel(
                                      "Common.OK"
                                    )}
                                  </Button>
                                </Modal.Footer>
                              </Modal>
                            </React.Fragment>
                          </Card>
                        )}
                      </div>
                    )}
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Container fluid className="p-0">
          <Card>
            <Card.Header>
              <Card.Title className="mb-0">
                {adminGenericDetailsScreenCMS?.title}
              </Card.Title>
            </Card.Header>
            <Card.Body>
              {candidateDetailsList && candidateDetailsList.length > 0 ? (
                <CandidatesListPagination
                  columns={tableColumns && tableColumns}
                  data={candidateDetailsList}
                  cmsData={adminGenericDetailsScreenCMS}
                />
              ) : (
                fetchGenericDetailsScreenLabel('GenericDetailsScreen.NoCandidateData')
              )}
            </Card.Body>
          </Card>
        </Container>
      </Container>
    </React.Fragment>
  );
};

export default AccountDetailsScreen;